import { FeatureFlag } from '@revelio/core';
import { ReportTypeEnum } from '@revelio/data-access';

import type { ReportPageConfig } from '../../CustomDash/components/PageConfigProvider/types';
import { getLocalReportTemplate } from '../../CustomDash/templates/report-templates-registry';

export type MiddlewareReplacementPromotion = {
  readonly kind: 'middlewareReplacement';
  readonly middlewareReportType: ReportTypeEnum;
  readonly templateId: string;
  readonly featureFlag: FeatureFlag;
};

export type SectionCardPromotion = {
  readonly kind: 'sectionCard';
  readonly sectionCategory: string;
  readonly selectionReportType: string;
  readonly templateId: string;
  readonly featureFlag: FeatureFlag;
  readonly cardLabel: string;
  readonly cardDetail: string;
  readonly requiresCustomReports?: boolean;
  readonly enabledByCustomReports?: boolean;
};

export type ReportTemplatePromotion =
  | MiddlewareReplacementPromotion
  | SectionCardPromotion;

export const REPORT_TEMPLATE_PROMOTIONS: readonly ReportTemplatePromotion[] = [
  {
    kind: 'middlewareReplacement',
    middlewareReportType: ReportTypeEnum.MarketTightness,
    templateId: 'report-company-market-conditions',
    featureFlag: FeatureFlag.PromoteMarketConditionsReportTemplate,
  },
  {
    kind: 'middlewareReplacement',
    middlewareReportType: ReportTypeEnum.Sentiment,
    templateId: 'report-company-sentiment',
    featureFlag: FeatureFlag.PromoteSentimentReportTemplate,
  },
  {
    kind: 'sectionCard',
    sectionCategory: 'company',
    selectionReportType: 'report-org-health',
    templateId: 'report-org-health',
    featureFlag: FeatureFlag.PromoteOrgHealthReportTemplate,
    cardLabel: '[Beta]: Organizational Health',
    cardDetail:
      'Workforce intelligence analysis revealing operational execution, productivity enhancement, employee sentiment, compensation strategy, and geographic footprint',
    requiresCustomReports: false,
    enabledByCustomReports: true,
  },
];

const isPromotionActive = (
  p: ReportTemplatePromotion,
  flags: Record<FeatureFlag, boolean>,
  customReportsEnabled: boolean
): boolean => {
  if (
    p.kind === 'sectionCard' &&
    p.enabledByCustomReports &&
    customReportsEnabled
  ) {
    return true;
  }
  if (!flags[p.featureFlag]) {
    return false;
  }
  if (p.kind === 'sectionCard') {
    const needsCustom = p.requiresCustomReports !== false;
    if (needsCustom && !customReportsEnabled) {
      return false;
    }
  }
  return true;
};

export const getPromotedTemplateIds = (
  flags: Record<FeatureFlag, boolean>,
  customReportsEnabled: boolean
): Set<string> =>
  new Set(
    REPORT_TEMPLATE_PROMOTIONS.filter((p) =>
      isPromotionActive(p, flags, customReportsEnabled)
    ).map((p) => p.templateId)
  );

export type PromotedSectionCardColumn = {
  readonly id: string;
  readonly label: string;
  readonly detail: string;
  readonly templateId: string;
  readonly sampleFilterGroups?: string;
};

export const getPromotedSectionCardsForCategory = (
  category: string,
  flags: Record<FeatureFlag, boolean>,
  customReportsEnabled: boolean
): readonly PromotedSectionCardColumn[] =>
  REPORT_TEMPLATE_PROMOTIONS.filter(
    (p): p is SectionCardPromotion =>
      p.kind === 'sectionCard' &&
      p.sectionCategory === category &&
      isPromotionActive(p, flags, customReportsEnabled)
  ).map((p) => {
    const template = getLocalReportTemplate(p.templateId);
    return {
      id: p.selectionReportType,
      label: p.cardLabel,
      detail: p.cardDetail,
      templateId: p.templateId,
      sampleFilterGroups: template?.sampleFilterGroups,
    };
  });

export const getPromotedTemplateForSelectionReportType = (
  selectionReportType: string,
  flags: Record<FeatureFlag, boolean>,
  customReportsEnabled: boolean
): ReportPageConfig | null => {
  const entry = REPORT_TEMPLATE_PROMOTIONS.find((p) => {
    if (!isPromotionActive(p, flags, customReportsEnabled)) {
      return false;
    }
    if (p.kind === 'middlewareReplacement') {
      return p.middlewareReportType === selectionReportType;
    }
    return p.selectionReportType === selectionReportType;
  });
  if (!entry) {
    return null;
  }
  const template = getLocalReportTemplate(entry.templateId);
  return template ?? null;
};

export const getPromotedTemplateForReportOfferClick = (
  selectionReportType: string,
  flags: Record<FeatureFlag, boolean>,
  customReportsEnabled: boolean
): ReportPageConfig | null =>
  getPromotedTemplateForSelectionReportType(
    selectionReportType,
    flags,
    customReportsEnabled
  );

export const isPromotedTemplateSelection = (
  selectedTemplate: ReportPageConfig | null,
  flags: Record<FeatureFlag, boolean>,
  customReportsEnabled: boolean
): boolean =>
  selectedTemplate !== null &&
  REPORT_TEMPLATE_PROMOTIONS.some(
    (p) =>
      isPromotionActive(p, flags, customReportsEnabled) &&
      p.templateId === selectedTemplate.id
  );

export const getVirtualMiddlewareReportSelection = (
  selectedReportTypeFromStore: string[],
  selectedCustomReportTemplate: ReportPageConfig | null,
  flags: Record<FeatureFlag, boolean>,
  customReportsEnabled: boolean
): string[] => {
  if (selectedReportTypeFromStore.length > 0) {
    return selectedReportTypeFromStore;
  }
  if (!selectedCustomReportTemplate) {
    return [];
  }
  const entry = REPORT_TEMPLATE_PROMOTIONS.find(
    (p) =>
      isPromotionActive(p, flags, customReportsEnabled) &&
      p.templateId === selectedCustomReportTemplate.id
  );
  if (!entry) {
    return [];
  }
  if (entry.kind === 'middlewareReplacement') {
    return [entry.middlewareReportType];
  }
  return [entry.selectionReportType];
};
